import { useTranslation } from 'react-i18next'
import Tippy from '@tippyjs/react'

import Icon from '../../../../ui/Icon'
import { Button } from '../../../../buttons/Button'
import { Buttons } from '../../../../buttons/Buttons'

import { PartnerStatus, PartnerModel } from '../../../../../types/court/court'
import { ActionType } from '../../../../../constants/actionTypes'
import { initials } from '../../../../../utils/profile/profile'

type PartnerCardProps = {
  partner: PartnerModel
  onAction: (partner: PartnerModel, action: ActionType) => void
}

const PartnerCard = ({ partner, onAction }: PartnerCardProps) => {
  const { t } = useTranslation('partners')
  const isLinked = partner.status === PartnerStatus.LINKED
  const isRequestSent = partner.status === PartnerStatus.REQUEST_SENT
  const isRequestReceived = partner.status === PartnerStatus.REQUEST_RECEIVED
  const isBlocked = partner.status === PartnerStatus.BLOCKED
  const fullName = `${partner.firstName ?? ''} ${partner.lastName ?? ''}`.trim()
  const displayName = fullName || partner.email

  return (
    <div className="ph-card list-partner-card">
      {partner.favorite && (
        <span className="badge badge-accent partner-card-favorite-badge">
          {t('partners.card.favorite-badge')}
        </span>
      )}
      <div
        className={`ph-row partner-card-header${partner.favorite ? ' has-favorite' : ''}`}
      >
        <div className="ph-avatar lg">{initials(displayName)}</div>
        <div className="partner-card-details">
          <div className="partner-card-name">{displayName}</div>
          <div className="mono tiny muted">{partner.email}</div>
          <div className="mono tiny partner-card-status">
            {isLinked ? (
              <span className="badge badge-success">
                {t('partners.card.linked')}
              </span>
            ) : isRequestReceived ? (
              <span className="badge badge-info">
                {t('partners.card.request-received')}
              </span>
            ) : isBlocked ? (
              <span className="badge badge-danger">
                {t('partners.card.blocked')}
              </span>
            ) : (
              <span className="badge badge-warning">
                {t('partners.card.invite-pending')}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="ph-row ph-between partner-card-stats">
        <div>
          <span className="muted">{t('partners.card.games-played')} </span>
          <span className="display partner-card-value">
            {partner.gamesPlayed}
          </span>
        </div>
        <div>
          <span className="muted">{t('partners.card.win-rate')} </span>
          <span className="display partner-card-value">{partner.winRate}%</span>
        </div>
      </div>
      <div className="divider" />
      <Buttons className="partner-card-actions">
        {isLinked && (
          <Button
            label={t('partners.card.book')}
            icon={<Icon name="calendar" size={13} />}
            variant="primary"
            size="sm"
            className="partner-card-book"
            onClick={() => onAction(partner, ActionType.BOOK)}
          />
        )}
        {isRequestReceived && (
          <>
            <Button
              label={t('partners.card.accept')}
              icon={<Icon name="check" size={13} />}
              variant="primary"
              size="sm"
              className="partner-card-book"
              onClick={() => onAction(partner, ActionType.ACCEPT)}
            />
            <Tippy
              content={t('partners.card.reject')}
              placement="top"
              className="ph-tooltip"
            >
              <Button
                label={t('partners.card.reject')}
                icon={<Icon name="x" size={13} />}
                variant="ghost"
                size="sm"
                className="ph-iconbtn"
                iconOnly
                onClick={() => onAction(partner, ActionType.REJECT)}
              />
            </Tippy>
          </>
        )}
        {isRequestSent && (
          <Button
            label={t('partners.card.cancel-request')}
            icon={<Icon name="x" size={13} />}
            variant="primary"
            size="sm"
            className="w-100"
            onClick={() => onAction(partner, ActionType.CANCEL_REQUEST)}
          />
        )}
        {isLinked && (
          <Tippy
            content={
              partner.favorite
                ? t('partners.card.unfavorite')
                : t('partners.card.favorite')
            }
            placement="top"
            className="ph-tooltip"
          >
            <Button
              label={
                partner.favorite
                  ? t('partners.card.unfavorite')
                  : t('partners.card.favorite')
              }
              icon={
                <Icon
                  name="star"
                  size={partner.favorite ? 18 : 15}
                  strokeWidth={1.7}
                  className={
                    partner.favorite ? 'partner-card-favorite' : undefined
                  }
                />
              }
              variant="ghost"
              size="sm"
              className="ph-iconbtn"
              iconOnly
              onClick={() => onAction(partner, ActionType.FAVORITE)}
            />
          </Tippy>
        )}
        {isLinked && (
          <Tippy
            content={t('partners.card.remove')}
            placement="top"
            className="ph-tooltip"
          >
            <Button
              label={t('partners.card.remove')}
              icon={<Icon name="trash" size={15} />}
              variant="ghost"
              size="sm"
              className="ph-iconbtn"
              iconOnly
              onClick={() => onAction(partner, ActionType.DELETE)}
            />
          </Tippy>
        )}
        {(isLinked || isRequestReceived) && (
          <Tippy
            content={t('partners.card.block')}
            placement="top"
            className="ph-tooltip"
          >
            <Button
              label={t('partners.card.block')}
              icon={<Icon name="block" size={15} />}
              variant="ghost"
              size="sm"
              className="ph-iconbtn"
              iconOnly
              onClick={() => onAction(partner, ActionType.BLOCK)}
            />
          </Tippy>
        )}
        {isBlocked && (
          <Button
            label={t('partners.card.unblock')}
            icon={<Icon name="block" size={13} />}
            variant="primary"
            size="sm"
            className="partner-card-book"
            onClick={() => onAction(partner, ActionType.UNBLOCK)}
          />
        )}
      </Buttons>
    </div>
  )
}

export default PartnerCard
